import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BonsService } from '../../../services/bons.service';

type GroupStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

interface BonDenomination {
  id: string;
  faceValue: number;
  salePrice: number;
  tokenId: string | null;
  status: GroupStatus;
  metadataFrozen: boolean;
  validityDays: number;
  maxPerOrder: number | null;
  stockTotal: number | null;
  stockSold: number;
  createdDate: string;
}


interface DiscountTier {
  minAmount: number;
  percent: number;
}

interface BonGroup {
  groupId: string;
  label: string;
  description: string | null;
  imageUrl: string | null;
  discountTiers: DiscountTier[] | null;
  generalDiscount: number | null;
  status: GroupStatus;
  validityDays: number;
  maxPerOrder: number | null;
  stockTotal: number | null;

  denominations: BonDenomination[];
}

interface BonGroupStats {
  emis: number;
  consommes: number;
  enCirculation: number;
  encoursFaceValue: number;
  encoursSalePrice: number;
  byDenomination: {
    productId: string;
    faceValue: number;
    emis: number;
    consommes: number;
    enCirculation: number;
    encoursFaceValue: number;
    encoursSalePrice: number;
  }[];
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Brouillon',
  PENDING_APPROVAL: "En attente d'approbation",
  ACTIVE: 'Actif',
  SUSPENDED: 'Suspendu',
  ARCHIVED: 'Archivé',
};

@Component({
  selector: 'app-bons',
  templateUrl: './bons.component.html',
  styleUrls: ['./bons.component.css'],
  providers: [MessageService],
})
export class BonsComponent implements OnInit {
  // --- Variables d'état ---
  search = '';
  statusFilter = 'ALL';
  showModal = false;
  showStatsModal = false;
  loading = false;
  loadingStats = false;
  errorMessage = '';

  // --- Données ---
  groups: BonGroup[] = [];
  editingGroup: BonGroup | null = null;
  selectedGroup: BonGroup | null = null;
  stats: BonGroupStats | null = null;

  // Popup de confirmation générique (remplace les window.confirm/alert) —
  // utilisée pour publier, dépublier et supprimer. openConfirm() prépare le
  // texte et l'action à exécuter ; confirmAction() l'exécute si le marchand
  // valide, closeConfirm() annule.
  confirmDialog: {
    visible: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    danger: boolean;
    action: (() => void) | null;
  } = {
    visible: false,
    title: '',
    message: '',
    confirmLabel: 'Confirmer',
    danger: false,
    action: null,
  };


  newGroup: any = {
    label: 'Bon cadeau',
    description: '',
    imageUrl: '',
    discountTiers: [] as DiscountTier[],
    generalDiscount: null,
    validityMonths: 12,
    validityDays: 365,
    maxPerOrder: null,
    stockTotal: null,
  };

  // Champs de saisie pour ajouter un seuil de réduction
  tierMinInput: number | null = null;
  tierPercentInput: number | null = null;

  constructor(
    private bonsService: BonsService,
    private messageService: MessageService,
  ) {}

  ngOnInit() {
    this.loadGroups();
  }

  loadGroups() {
    this.bonsService.getGroups().subscribe({
      next: (res) => {
        this.groups = res.groups || [];
      },
      error: (err) => {
        console.error('Erreur chargement des bons', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger vos bons cadeaux.',
        });
      },
    });
  }

  statusLabel(status: string): string {
    return STATUS_LABELS[status] || status;
  }

  // --- Popup de confirmation générique ---
  openConfirm(title: string, message: string, action: () => void, confirmLabel = 'Confirmer', danger = false) {
    this.confirmDialog = { visible: true, title, message, confirmLabel, danger, action };
  }

  closeConfirm() {
    this.confirmDialog = {
      visible: false,
      title: '',
      message: '',
      confirmLabel: 'Confirmer',
      danger: false,
      action: null,
    };
  }

  confirmAction() {
    const action = this.confirmDialog.action;
    this.closeConfirm();
    if (action) action();
  }

  // Modifiable en DRAFT (jamais encore publié) et en SUSPENDED (dépublié —
  // voir canUnpublish/unpublish) : dans les deux cas rien n'est en ligne sur
  // la marketplace, donc les termes peuvent encore changer. Plus modifiable
  // une fois ACTIVE (voir MerchantVoucherService.updateGroup/publishGroup).
  canEdit(group: BonGroup): boolean {
    return group.status === 'DRAFT' || group.status === 'SUSPENDED';
  }

  canPublish(group: BonGroup): boolean {
    return group.status === 'DRAFT' || group.status === 'SUSPENDED';
  }

  // "Dépublier" retire le bon de la marketplace SANS le supprimer —
  // réversible, republiable à tout moment (canPublish couvre déjà SUSPENDED).
  // Uniquement depuis ACTIVE.
  canUnpublish(group: BonGroup): boolean {
    return group.status === 'ACTIVE';
  }

  // "Supprimer" archive le bon (DELETE /merchant/vouchers/:groupId) de façon
  // TERMINALE — plus possible de le republier ensuite. Les bons déjà achetés
  // restent valides. Un bon ACTIVE doit d'abord être dépublié (voir
  // canUnpublish) ; DRAFT/SUSPENDED sont supprimables directement.
  canDelete(group: BonGroup): boolean {
    return group.status === 'DRAFT' || group.status === 'SUSPENDED';
  }


  denominationsSummary(group: BonGroup): string {
    if (!group.denominations.length) return 'Aucune vente pour l\'instant';
    return group.denominations
      .map((d) => d.faceValue)
      .sort((a, b) => a - b)
      .map((v) => (v >= 1000 ? `${v / 1000}K` : String(v)))
      .join(' / ');
  }


  // Résumé affiché dans le tableau : réduction générale (si définie) puis seuils.
  discountSummary(group: BonGroup): string {
    const parts: string[] = [];
    if (group.generalDiscount) parts.push(`${group.generalDiscount}% sur tout montant`);
    const tiers = this.discountTiersSummary(group);
    if (tiers !== '—') parts.push(tiers);
    return parts.length ? parts.join(' / ') : '—';
  }

  discountTiersSummary(group: BonGroup): string {
    const tiers = group.discountTiers;
    if (!tiers || tiers.length === 0) return '—';
    return tiers
      .slice()
      .sort((a, b) => a.minAmount - b.minAmount)
      .map((t) => {
        const min = t.minAmount >= 1000 ? `${t.minAmount / 1000}K` : String(t.minAmount);
        return `${t.percent}% à partir de ${min}`;
      })
      .join(' / ');
  }

  filteredGroups(): BonGroup[] {
    return this.groups.filter((g) => {
      const matchesSearch = g.label.toLowerCase().includes(this.search.toLowerCase());
      const matchesStatus = this.statusFilter === 'ALL' || g.status === this.statusFilter;
      return matchesSearch && matchesStatus;
    });
  }

  // --- Ajout/retrait de seuils de réduction ---
  addDiscountTier() {
    const min = Number(this.tierMinInput);
    const percent = Number(this.tierPercentInput);

    if (!min || min < 0) return;
    if (!percent || percent <= 0 || percent > 99) return;
    // Pas deux seuils au même montant de départ.
    if (this.newGroup.discountTiers.some((t: DiscountTier) => t.minAmount === min)) return;

    this.newGroup.discountTiers = [
      ...this.newGroup.discountTiers,
      { minAmount: min, percent },
    ].sort((a: DiscountTier, b: DiscountTier) => a.minAmount - b.minAmount);

    this.tierMinInput = null;
    this.tierPercentInput = null;
  }

  removeDiscountTier(index: number) {
    this.newGroup.discountTiers = this.newGroup.discountTiers.filter((_: DiscountTier, i: number) => i !== index);
  }

  // --- Modale création/édition ---
  openCreateModal() {
    this.editingGroup = null;
    this.resetForm();
    this.showModal = true;
  }

  openEditModal(group: BonGroup) {
    if (!this.canEdit(group)) return;
    this.editingGroup = group;
    const validityMonths = Math.max(1, Math.round((group.validityDays ?? 365) / 30));
    this.newGroup = {
      label: group.label || 'Bon cadeau',
      description: group.description || '',
      imageUrl: group.imageUrl || '',
      discountTiers: (group.discountTiers || []).map((t) => ({ ...t })),
      generalDiscount: group.generalDiscount ?? null,
      validityMonths,
      validityDays: group.validityDays ?? 365,
      maxPerOrder: group.maxPerOrder ?? null,
      stockTotal: group.stockTotal ?? null,
    };
    this.tierMinInput = null;
    this.tierPercentInput = null;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.editingGroup = null;
    this.resetForm();
  }

  resetForm() {
    this.errorMessage = '';
    this.tierMinInput = null;
    this.tierPercentInput = null;
    this.newGroup = {
      label: 'Bon cadeau',
      description: '',
      imageUrl: '',
      discountTiers: [],
      generalDiscount: null,
      validityMonths: 12,
      validityDays: 365,
      maxPerOrder: null,
      stockTotal: null,
    };
  }

  save() {
    const tiers = this.newGroup.discountTiers as DiscountTier[];
    const rawGeneralDiscount = this.newGroup.generalDiscount;
    const generalDiscount = rawGeneralDiscount === null || rawGeneralDiscount === undefined || rawGeneralDiscount === ''
      ? null
      : Number(rawGeneralDiscount);

    if (generalDiscount !== null && (Number.isNaN(generalDiscount) || generalDiscount < 0 || generalDiscount > 99)) {
      this.errorMessage = 'La réduction générale doit être comprise entre 0 et 99%.';
      return;
    }

    for (const t of tiers) {
      if (t.percent < 0 || t.percent > 99) {
        this.errorMessage = 'Chaque réduction doit être comprise entre 0 et 99%.';
        return;
      }
    }
    // Un seuil moins avantageux que la réduction générale ne servirait à rien
    // (le seuil remplace la générale, il ne s'y ajoute pas).
    if (generalDiscount) {
      const useless = tiers.find((t) => t.percent <= generalDiscount);
      if (useless) {
        this.errorMessage = `Le seuil à ${useless.percent}% n'apporte rien : il doit dépasser la réduction générale (${generalDiscount}%).`;
        return;
      }
    }
    // Deux seuils ne peuvent pas commencer au même montant.
    const minAmounts = tiers.map((t) => t.minAmount);
    if (new Set(minAmounts).size !== minAmounts.length) {
      this.errorMessage = 'Deux seuils ne peuvent pas commencer au même montant.';
      return;
    }

    const validityMonths = Number(this.newGroup.validityMonths ?? 1);
    const validityDays = Math.max(1, validityMonths * 30);

    this.loading = true;

    if (this.editingGroup) {
      // L'édition ne porte que sur les champs partagés — jamais sur les
      // montants vendables, fixés à la création (voir canEdit).
      const payload = {
        label: this.newGroup.label || 'Bon cadeau',
        description: this.newGroup.description || undefined,
        imageUrl: this.newGroup.imageUrl || undefined,
        discountTiers: tiers,
        // 0 = retirer la réduction générale (undefined = "inchangé" côté back).
        generalDiscount: generalDiscount ?? 0,
        validityDays,
        maxPerOrder: this.newGroup.maxPerOrder ? Number(this.newGroup.maxPerOrder) : undefined,
        stockTotal: this.newGroup.stockTotal ? Number(this.newGroup.stockTotal) : undefined,
      };
      this.bonsService.updateGroup(this.editingGroup.groupId, payload).subscribe({
        next: () => this.onSaveSuccess('Bon mis à jour.'),
        error: (err) => this.onSaveError(err),
      });
      return;
    }

    const payload = {
      label: this.newGroup.label || 'Bon cadeau',
      description: this.newGroup.description || undefined,
      imageUrl: this.newGroup.imageUrl || undefined,
      discountTiers: tiers,
      // 0 = retirer la réduction générale (undefined = "inchangé" côté back).
      generalDiscount: generalDiscount ?? 0,
      validityDays,
      maxPerOrder: this.newGroup.maxPerOrder ? Number(this.newGroup.maxPerOrder) : undefined,
      stockTotal: this.newGroup.stockTotal ? Number(this.newGroup.stockTotal) : undefined,
    };
    this.bonsService.createGroup(payload).subscribe({
      next: () => this.onSaveSuccess('Bon créé — encore en brouillon, publiez-le pour le mettre en ligne sur la marketplace.'),
      error: (err) => this.onSaveError(err),
    });
  }

  private onSaveSuccess(detail: string) {
    this.loading = false;
    this.messageService.add({ severity: 'success', summary: 'Succès', detail });
    this.loadGroups();
    this.closeModal();
  }

  private onSaveError(err: any) {
    this.loading = false;
    this.errorMessage = err.error?.message || "Erreur lors de l'enregistrement.";
  }

  // --- Publication ---
  publish(group: BonGroup) {
    if (!this.canPublish(group)) return;
    this.openConfirm(
      'Publier le bon',
      `Publier "${group.label}" ? Il sera visible et achetable sur la marketplace.`,
      () => this.doPublish(group),
      'Publier',
    );
  }

  private doPublish(group: BonGroup) {
    this.bonsService.publishGroup(group.groupId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Publié',
          detail: 'Votre bon est maintenant en ligne sur la marketplace.',
        });
        this.loadGroups();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors de la publication.',
        });
      },
    });
  }

  // --- Dépublication ---
  // Retire le bon de la marketplace SANS le supprimer : réversible, il
  // redevient modifiable (voir canEdit) et reste republiable à tout moment
  // (bouton "Publier" réapparaît, voir canPublish). Différent de
  // "Supprimer" (terminal) — voir canUnpublish/canDelete.
  unpublish(group: BonGroup) {
    if (!this.canUnpublish(group)) return;
    this.openConfirm(
      'Dépublier le bon',
      `Dépublier "${group.label}" ? Il disparaîtra de la marketplace, redeviendra modifiable, et vous pourrez le republier à tout moment.`,
      () => this.doUnpublish(group),
      'Dépublier',
    );
  }

  private doUnpublish(group: BonGroup) {
    this.bonsService.unpublishGroup(group.groupId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Dépublié',
          detail: 'Votre bon a été retiré de la marketplace. Vous pouvez le modifier puis le republier à tout moment.',
        });
        this.loadGroups();
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors de la dépublication.',
        });
      },
    });
  }

  // --- Suppression ---
  // Supprime le bon côté marchand, DÉFINITIVEMENT : il disparaît aussi de la
  // marketplace et ne pourra plus être republié. Le back refuse tant que le
  // bon est ACTIVE — il faut d'abord le dépublier (voir canDelete() pour la
  // garde UI correspondante).
  deleteGroup(group: BonGroup) {
    if (!this.canDelete(group)) return;
    this.openConfirm(
      'Supprimer le bon',
      `Supprimer définitivement "${group.label}" ? Cette action est irréversible.`,
      () => this.doDelete(group),
      'Supprimer',
      true,
    );
  }

  private doDelete(group: BonGroup) {
    this.bonsService.deleteGroup(group.groupId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Supprimé',
          detail: 'Le bon a été supprimé et retiré de la marketplace.',
        });
        this.loadGroups();
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error?.message || 'Erreur lors de la suppression.',
        });
      },
    });
  }

  // --- Statistiques ---
  openStats(group: BonGroup) {
    this.selectedGroup = group;
    this.stats = null;
    this.loadingStats = true;
    this.showStatsModal = true;
    this.bonsService.getGroupStats(group.groupId).subscribe({
      next: (res) => {
        this.stats = res;
        this.loadingStats = false;
      },
      error: () => {
        this.loadingStats = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de charger les statistiques.',
        });
      },
    });
  }

  closeStatsModal() {
    this.showStatsModal = false;
    this.selectedGroup = null;
    this.stats = null;
  }
}